/**
 * rondo-web-forms
 *
 * Rondo Web Forms
 *
 * @author Jiri Hybek <jiri@hybek.cz>
 * @license See LICENSE file distributed with this source code.
 */

import { v4 as uuid_v4 } from "uuid";

import { FormElement } from "../FormElement";
import { IFormElementConfig } from "../IFormElementConfig";
import { IFormElementFactory } from "../IFormElementFactory";
import { FormModel } from "../FormModel";
import { FORM_EL_TYPE } from "../FormElementTypes";
import { IFormElementPath } from "../IFormElementPath";
import { arrayMove } from "../../util";

/**
 * Row list item
 */
export interface IFormRowListItem {
	id: string;
	content: Array<FormElement<any>>;
}

/**
 * Element config
 */
export interface IFormRowListConfig extends IFormElementConfig {
	/** Section contents */
	content?: Array<IFormElementFactory<any>>;
}

/**
 * Row dynamic list
 */
export class FormRowList extends FormElement<IFormRowListConfig> {

	/** Field value ID */
	protected valueId: string;

	/** Items */
	protected items: Array<IFormRowListItem> = [];

	/** Virtual path cache */
	private virtualPath: IFormElementPath = null;

	/**
	 * Element constructor
	 *
	 * @param model Model instance
	 * @param parent Parent element
	 * @param config Element configuration
	 */
	public constructor(
		protected model: FormModel,
		protected parent: FormElement<any>,
		protected config: IFormRowListConfig
	) {

		super(model, parent, FORM_EL_TYPE.ROW_LIST, config);

		this.valueId = this.path
			.filter((item) => item.id ? true : false)
			.map((item) => item.id)
			.join("_");

	}

	/**
	 * Returns element path which should be used for child elements
	 */
	public getVirtualPath() {

		return this.virtualPath;

	}

	/**
	 * Returns items as a valid model value
	 */
	protected getModelValue() {

		return this.items.map((item) => item.id);

	}

	/**
	 * Updates the element
	 *
	 * @param resetState If to reset state
	 */
	public update(resetState: boolean = false) {

		// Get current items
		let hasChanged = false;
		let newValue : Array<string> = this.model.get(this.valueId);

		if (!(newValue instanceof Array)) {
			newValue = [];
		}

		const rowIndex: { [K: string]: IFormRowListItem } = {};
		this.items.forEach((item) => rowIndex[item.id] = item);

		// Re-create items array
		const newItems: Array<IFormRowListItem> = [];

		for (let i = 0; i < newValue.length; i++) {

			const itemId = newValue[i];

			// Re-use and insert an already existing instance
			if (rowIndex[itemId]) {

				// console.log("LIST ITEM REUSE", itemId);

				newItems.push(rowIndex[itemId]);
				delete rowIndex[itemId];

			// Create a new row instance
			} else {

				// console.log("LIST ITEM CREATE", itemId);

				const newRow: IFormRowListItem = {
					id: itemId,
					content: []
				};

				this.virtualPath = this.getPath().concat([{
					id: itemId,
					title: ""
				}])

				if (this.config.content) {

					for (let j = 0; j < this.config.content.length; j++) {
						newRow.content.push(
							this.config.content[j](this.model, this)
						);
					}

				}

				this.virtualPath = null;
				newItems.push(newRow);

				hasChanged = true;

			}

		}

		// Destroy redundant rows
		for (const k in rowIndex) {

			// console.log("LIST ITEM DESTROY", k);

			for (let i = 0; i < rowIndex[k].content.length; i++) {
				rowIndex[k].content[i].destroy();
			}

			hasChanged = true;

		}

		// Check order change
		for (let i = 0; i < this.items.length; i++) {
			if (!newItems[i] || newItems[i].id !== this.items[i].id) {
				hasChanged = true;
			}
		}

		this.items = newItems;

		// Call update on all item elements
		for (let j = 0; j < this.items.length; j++) {

			for (let i = 0; i < this.items[j].content.length; i++) {
				this.items[j].content[i].update(resetState);
			}

		}

		if (hasChanged) {
			// console.log("LIST CHANGED");
			this.emit("update");
		}

	}

	/**
	 * Adds a new row
	 */
	public add() {

		const value = this.getModelValue().concat([ uuid_v4() ]);
		this.model.set(this.valueId, value, true);

	}

	/**
	 * Removes a row
	 *
	 * @param rowId Row ID
	 */
	public remove(rowId: string) {

		const value = this.getModelValue().filter((itemId) => itemId !== rowId);
		this.model.set(this.valueId, value, true);

	}

	/**
	 * Moves the item up (decement index)
	 *
	 * @param rowId Row ID
	 */
	public moveUp(rowId: string) {

		const value = this.getModelValue();
		const index = value.indexOf(rowId);

		if (index < 0) {
			return;
		}

		arrayMove(value, index, Math.max(0, index - 1));

		this.model.set(this.valueId, value, true);

	}

	/**
	 * Moves the item down (increment index)
	 *
	 * @param rowId Row ID
	 */
	public moveDown(rowId: string) {

		const value = this.getModelValue();
		const index = value.indexOf(rowId);

		if (index < 0) {
			return;
		}

		arrayMove(value, index, Math.min(value.length - 1, index + 1));

		this.model.set(this.valueId, value, true);

	}

	/**
	 * Returns element state
	 */
	public getState() {

		return {
			...super.getState(),
			items: this.items
		};

	}

	/**
	 * Destroys the element
	 */
	public destroy() {

		// Call destroy on all content elements
		for (let j = 0; j < this.items.length; j++) {

			for (let i = 0; i < this.items[j].content.length; i++) {
				this.items[j].content[i].destroy();
			}

		}

		super.destroy();

	}

	/**
	 * Returns row contents by ID
	 *
	 * @param rowId Row ID
	 */
	public getRowContentById(rowId: string) {

		for (let i = 0; i < this.items.length; i++) {
			if (this.items[i].id === rowId) {
				return this.items[i].content;
			}
		}

		return null;

	}

}

/**
 * Declares row list
 *
 * @param config Config
 * @param content Content elements
 */
export function RowList(
	config: IFormRowListConfig,
	content: Array<IFormElementFactory<any>> = []
) {

	const _config = {
		content: content,
		...config
	};

	return (model, parent) => new FormRowList(model, parent, _config);

}
